import {
  animate,
  animateChild,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
} from '@angular/router';
import { Platform } from '@model/platform.model';
import { PlatformService } from '@util/platform.service';
import { CustomerCareService } from '@api/customer-care.service';
import { PetSafeProduct } from '@model/product/product.model';

/**
 * CustomerComponent
 *
 * @export CustomerComponent
 */
@Component({
  selector: 'app-customer',
  templateUrl: './customer.component.html',
  styleUrls: ['./customer.component.scss'],
  animations: [
    trigger('slide', [
      transition('void => *', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate('0.1s ease-in'),
      ]),
    ]),
    trigger('fade', [
      transition('void => *', [style({ opacity: 0 }), animate(200)]),
    ]),
    trigger('stagger', [
      transition('void => *', [
        query('div', stagger('-.005s ease-in', [animateChild()])),
      ]),
    ]),
  ],
})
export class CustomerComponent implements OnInit {
  searchingCustomer = false;
  searchingProduct = false;
  hasProducts = false;
  /**
   * used to valid if the url id param has changed
   */
  oldId: string;

  /**
   * the string id of the selected customer
   */
  cId: string;

  urlProduct: string;

  selectedCustomerProduct: {
    createdAt: string;
    createdBy: string;
    entityData: {
      connectionStatus: string;
      friendlyName: string;
      lastSeenOn: string;
      productType: string;
      sessionIdentifier: string;
    };
    entityId: string;
    entityType: 'PRODUCT';
    groupId: string;
    updatedAt: string;
    updatedBy: string;
  } = null;

  /**
   * the selected product owned by the customer
   */
  selectedProduct: PetSafeProduct;

  user;
  /**
   * Creates an instance of CustomerComponent.
   *
   * @param router used to get url parameter
   * @param route snapshot contining all url params
   * @param customerService instance of CustomerService
   * @param platformService instance of PlatformService
   */
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private platformService: PlatformService,
    private customerCareService: CustomerCareService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.router.events.subscribe(res => {
      if (res instanceof NavigationEnd) {
        this.cId = this.route.snapshot.paramMap.get('id');
        this.urlProduct = this.route.snapshot.paramMap.get('product');
        if (this.oldId !== this.cId) {
          this.oldId = this.cId;
          this.initCustomer();
        }
      }
    });
  }

  /**
   * ngOnInit
   *
   * Initialises the startDate for getting Activities to be 60 days before today
   */
  ngOnInit(): void {}

  /**
   * Initialises Customer Object using the url id param.
   * Uses the in-memory cache if availble
   */
  initCustomer(): void {
    if (this.cId) {
      this.searchingCustomer = true;
      this.customerCareService
        .getAccount(this.cId)
        .subscribe(value => {
          this.user = value.data;
          if (this.urlProduct) {
            this.selectedCustomerProduct = value.data.products.find(
              p => p.entityId === this.urlProduct,
            );
            this.selectProduct();
          } else if (value.data.products.length > 0) {
            this.hasProducts = true;
            this.selectedCustomerProduct = value.data.products[0];
            this.selectProduct();
          }
          this.searchingCustomer = false;
        });
    }
  }

  /**
   * Refresh Local Cache
   */
  refresh(): void {
    this.initCustomer();
  }

  updateUrl() {
    this.urlProduct = this.selectedCustomerProduct.entityId;
    window.history.replaceState(
      {},
      '',
      `/customer/${this.cId}/${this.urlProduct}`,
    );
  }

  /**
   * selectProduct
   * resets activity filters when a product is selected
   */
  selectProduct(): void {
    this.selectedProduct = null;
    this.updateUrl();
    switch (this.selectedCustomerProduct.entityData.productType) {
      case 'smartfeed':
      case 'smartfeed2':
        this.searchingProduct = true;
        this.customerCareService
          .getSmartFeedsByThingNames({
            thingNames: [this.selectedCustomerProduct.entityId],
          })
          .subscribe(
            value => {
              this.selectedProduct = value.data[0];
              this.searchingProduct = false;
            },
            error => {
              console.log(error);
              this.searchingProduct = false;
            },
          );
        break;
      case 'smartdogtrainer':
        this.searchingProduct = true;
        this.customerCareService
          .getSmartDogTrainersByThingNames([
            {
              thingName: this.selectedCustomerProduct.entityId,
              userId: this.user.userId,
            },
          ])
          .subscribe(
            value => {
              this.selectedProduct = value.data[0];
              this.searchingProduct = false;
            },
            error => {
              console.log(error);
              this.searchingProduct = false;
            },
          );
        break;
      case 'scoopfree':
      case 'scoopfree2':
        this.searchingProduct = true;
        this.customerCareService
          .getScoopfreesByThingNames({
            thingNames: [this.selectedCustomerProduct.entityId],
          })
          .subscribe(
            value => {
              this.selectedProduct = value.data[0];
              this.searchingProduct = false;
            },
            error => {
              console.log(error);
              this.searchingProduct = false;
            },
          );
        break;
      case 'smartdoor':
        this.searchingProduct = true;
        this.customerCareService
          .getSmartDoorsByThingNames({
            thingNames: [this.selectedCustomerProduct.entityId],
          })
          .subscribe(
            value => {
              this.selectedProduct = value.data[0];
              this.searchingProduct = false;
            },
            error => {
              console.log(error);
              this.searchingProduct = false;
            },
          );
        break;
    }
  }
  /**
   * checkProduct
   * returns the productType
   */
  checkProduct(productType: string): string {
    switch (productType || '') {
      case 'smartfeed':
      case 'smartfeed2':
        return 'smartfeed';
      case 'smartdogtrainer':
        return 'smartdogtrainer';
      case 'scoopfree':
      case 'scoopfree2':
        return 'scoopfree';
      case 'smartdoor':
        return 'smartdoor';
    }
    return 'nullProduct';
  }

  /**
   * getPlatform
   *
   * @param pId a string indicating the platforms id
   * @returns a Platform object with the matching id
   */
  getPlatform(pId: string): Platform {
    return this.platformService.getById(pId);
  }
}
